// src/admin/pages/TimeRTPSchedule.tsx — Time-Based RTP Scheduler UI
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import useAdmin from '../hooks/useAdmin';
import { Loader2, Clock, Plus, Trash2, AlertCircle } from 'lucide-react';
import { fetchCsrfToken } from '../../shared/api';

interface ScheduleSlot {
  id: string;
  hour_start: number;
  hour_end: number;
  rtp_value: number;
  label: string;
  is_active: boolean;
}
interface TableRow { id: string; name: string; manager_email?: string; }
interface NextChange { in_hours: number; at_hour: number; new_rtp: number; label: string; }

const HOUR_LABELS: Record<number, string> = {
  0: '12am', 6: '6am', 9: '9am', 12: '12pm',
  14: '2pm', 18: '6pm', 20: '8pm', 22: '10pm'
};
const PRESET_SCHEDULES = [
  { label: '🌙 Off-Peak Night', slots: [{ hour_start: 0, hour_end: 9, rtp_value: 88, label: 'Night' }] },
  { label: '☀️ Peak Evening', slots: [{ hour_start: 18, hour_end: 24, rtp_value: 97, label: 'Peak Evening' }] },
  { label: '📅 Full Day Split', slots: [
    { hour_start: 0,  hour_end: 9,  rtp_value: 88, label: 'Night' },
    { hour_start: 9,  hour_end: 18, rtp_value: 93, label: 'Day' },
    { hour_start: 18, hour_end: 24, rtp_value: 97, label: 'Peak' },
  ]},
];

const RTP_MIN_PERCENT = 50;
const RTP_MAX_PERCENT = 150;

export default function TimeRTPSchedule() {
  const { token, fetchTables, error, setError } = useAdmin();
  const [tables, setTables] = useState<TableRow[]>([]);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [schedule, setSchedule] = useState<ScheduleSlot[]>([]);
  const [nextChange, setNextChange] = useState<NextChange | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ hour_start: 0, hour_end: 8, rtp_value: 90, label: '' });

  useEffect(() => {
    if (!token) return;
    fetchTables().then(data => setTables(Array.isArray(data) ? data : [])).catch(() => {});
  }, [token, fetchTables]);

  const loadSchedule = useCallback(async (tableId: string) => {
    if (!tableId) return;
    setLoading(true);
    try {
      const res = await fetch(`/admin/tables/${tableId}/rtp-schedule`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: 'include'
      });
      const data = await res.json();
      setSchedule(data.schedule || []);
      setNextChange(data.nextChange || null);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }, [token, setError]);

  const getCsrf = async () => {
    try { return await fetchCsrfToken(); }
    catch { return ''; }
  };

  const saveSlot = async () => {
    if (!selectedTableId) { toast.error('Select a table first'); return; }
    if (form.hour_start >= form.hour_end) { toast.error('End hour must be after start'); return; }
    if (form.rtp_value < RTP_MIN_PERCENT || form.rtp_value > RTP_MAX_PERCENT) {
      toast.error(`RTP must be ${RTP_MIN_PERCENT}-${RTP_MAX_PERCENT}%`);
      return;
    }
    try {
      const csrf = await getCsrf();
      const res = await fetch(`/admin/tables/${selectedTableId}/rtp-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error((await res.json()).error?.message);
      toast.success('Schedule slot saved!');
      loadSchedule(selectedTableId);
      setForm({ hour_start: 0, hour_end: 8, rtp_value: 90, label: '' });
    } catch (err: any) { toast.error(err.message); }
  };

  const deleteSlot = async (hour_start: number) => {
    try {
      const csrf = await getCsrf();
      await fetch(`/admin/tables/${selectedTableId}/rtp-schedule/${hour_start}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf }, credentials: 'include'
      });
      toast.success('Slot deleted');
      loadSchedule(selectedTableId);
    } catch (err: any) { toast.error(err.message); }
  };

  const applyPreset = async (slots: typeof PRESET_SCHEDULES[0]['slots']) => {
    if (!selectedTableId) { toast.error('Select a table first'); return; }
    const csrf = await getCsrf();
    for (const slot of slots) {
      await fetch(`/admin/tables/${selectedTableId}/rtp-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CSRF-Token': csrf },
        credentials: 'include',
        body: JSON.stringify(slot)
      });
    }
    toast.success('Preset applied!');
    loadSchedule(selectedTableId);
  };

  // Timeline visual
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const getSlotAtHour = (h: number) => schedule.find(s => h >= s.hour_start && h < s.hour_end);

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Time-Based RTP Schedule</h2>
        <p className="text-gray-500 mt-1">Set different RTP values for different times of day — real casino floor strategy</p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex justify-between">
          <span>{error}</span><button onClick={() => setError('')} className="font-bold">✕</button>
        </div>
      )}

      {/* Table Selector */}
      <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Select Table</label>
        <select
          value={selectedTableId}
          onChange={e => { setSelectedTableId(e.target.value); if (e.target.value) loadSchedule(e.target.value); }}
          className="w-full md:w-72 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">— Choose a table —</option>
          {tables.map(t => <option key={t.id} value={t.id}>{t.name} {t.manager_email ? `(${t.manager_email})` : ''}</option>)}
        </select>
      </div>

      {selectedTableId && (
        <>
          {/* Next Change Banner */}
          {nextChange && (
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <Clock size={20} className="text-blue-600" />
              <span className="text-blue-800 font-medium">
                Next RTP change in <strong>{nextChange.in_hours}h</strong> at <strong>{nextChange.at_hour}:00</strong> → <strong>{nextChange.new_rtp}%</strong>
                {nextChange.label && ` (${nextChange.label})`}
              </span>
            </div>
          )}

          {/* 24-Hour Timeline */}
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4">24-Hour RTP Timeline</h3>
            <div className="flex gap-0.5 h-16 rounded-lg overflow-hidden border border-gray-200">
              {hours.map(h => {
                const slot = getSlotAtHour(h);
                const now = new Date().getHours();
                const isNow = h === now;
                return (
                  <div
                    key={h}
                    className={`flex-1 flex flex-col items-center justify-center text-xs relative
                      ${slot ? (slot.rtp_value >= 95 ? 'bg-green-400' : slot.rtp_value >= 90 ? 'bg-yellow-300' : 'bg-red-300') : 'bg-gray-100'}
                      ${isNow ? 'ring-2 ring-indigo-500 ring-inset' : ''}`}
                    title={slot ? `${h}:00 — ${slot.rtp_value}% (${slot.label})` : `${h}:00 — No schedule`}
                  >
                    {isNow && <div className="absolute top-0 left-0 right-0 h-0.5 bg-indigo-600" />}
                    {slot && <span className="font-bold text-gray-800" style={{ fontSize: '9px' }}>{slot.rtp_value}%</span>}
                    {HOUR_LABELS[h] && <span className="text-gray-500" style={{ fontSize: '8px' }}>{HOUR_LABELS[h]}</span>}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded inline-block" /> ≥95% RTP</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-300 rounded inline-block" /> 90-95% RTP</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-300 rounded inline-block" /> &lt;90% RTP</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-gray-100 border rounded inline-block" /> No schedule</span>
            </div>
          </div>

          {/* Presets */}
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3">Quick Presets</h3>
            <div className="flex flex-wrap gap-3">
              {PRESET_SCHEDULES.map(p => (
                <button key={p.label} onClick={() => applyPreset(p.slots)}
                  className="px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 text-sm font-medium">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Add Slot Form */}
          <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2"><Plus size={18} /> Add Schedule Slot</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Start Hour (0-23)</label>
                <input type="number" min="0" max="23" value={form.hour_start}
                  onChange={e => setForm(p => ({ ...p, hour_start: +e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">End Hour (1-24)</label>
                <input type="number" min="1" max="24" value={form.hour_end}
                  onChange={e => setForm(p => ({ ...p, hour_end: +e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">RTP Value (50-150%)</label>
                <input type="number" min={RTP_MIN_PERCENT} max={RTP_MAX_PERCENT} step="0.5" value={form.rtp_value}
                  onChange={e => setForm(p => ({ ...p, rtp_value: +e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Label (Optional)</label>
                <input type="text" placeholder="e.g. Peak Hours" value={form.label}
                  onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 text-sm" />
              </div>
            </div>
            <button onClick={saveSlot}
              className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm">
              Save Slot
            </button>
          </div>

          {/* Current Schedule */}
          <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
            <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">Current Schedule</h3>
              {loading && <Loader2 className="animate-spin text-indigo-600" size={20} />}
            </div>
            {schedule.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <AlertCircle size={32} className="mx-auto mb-2 text-gray-300" />
                No schedule set — table uses base RTP
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 text-gray-600 text-sm">
                  <tr>
                    <th className="p-3 text-left">Time Range</th>
                    <th className="p-3 text-center">RTP</th>
                    <th className="p-3 text-left">Label</th>
                    <th className="p-3 text-center">Status</th>
                    <th className="p-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.map(s => {
                    const now = new Date().getHours();
                    const isActive = now >= s.hour_start && now < s.hour_end;
                    return (
                      <tr key={s.id} className={`border-b hover:bg-gray-50 ${isActive ? 'bg-green-50' : ''}`}>
                        <td className="p-3 font-mono text-sm font-medium">{s.hour_start}:00 – {s.hour_end}:00</td>
                        <td className="p-3 text-center font-bold text-indigo-700">{s.rtp_value}%</td>
                        <td className="p-3 text-gray-600">{s.label || '—'}</td>
                        <td className="p-3 text-center">
                          {isActive
                            ? <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">ACTIVE NOW</span>
                            : <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">Scheduled</span>}
                        </td>
                        <td className="p-3 text-center">
                          <button onClick={() => deleteSlot(s.hour_start)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete slot">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
